import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import * as _ from 'lodash';
import { CompanyCostItem } from '../models/company-cost-item';
import { ItemGroup, CostItem } from '../models/cost-item';
import { InvoiceData } from '../models/invoice';
import { ProjectMedia } from '../models/project-media.model';
import {
  ProjectFilter,
  ProjectFilters,
  Project,
  ProjectSort,
} from '../models/project.model';
import { CommunicationManagerService } from './communication-manager.service';
import { CostItemService } from './cost-item.service';
import { ItemGroupService } from './item-group.service';
import { ProjectManagerService } from './project-manager.service';
import { FinanceService } from './finance.service';
import { MediaService } from './media.service';
import { LocalStorageService } from './storage.service';
import { LoadingService } from './loading.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProjectFinanceService {
  private readonly projectRoute = 'project';
  private readonly companyItemRoute = 'companyItem';
  private readonly invoiceRoute = 'invoice';

  projectSort$ = new BehaviorSubject<ProjectSort>({
    sortBy: 0,
    sortDirection: 0,
  });

  projectFilter$ = new BehaviorSubject<ProjectFilter>({
    budgetType: 'All',
    client: {
      _id: 'All',
      name: 'All',
    },
    projectStatus: { _id: 'All', name: 'All' },
  });
  totalConst$ = new BehaviorSubject<{
    recorded: number;
    expected: number;
  }>({
    expected: 1,
    recorded: 1,
  });
  filters$ = new BehaviorSubject<ProjectFilters>({
    budgetTypes: ['All'],
    clients: [{ _id: 'All', name: 'All' }],
    projectStatuses: [
      { _id: 'All', name: 'All' },
      { _id: '0', name: 'Pitching' },
      { _id: '1', name: 'Ongoing' },
      { _id: '2', name: 'Completed' },
      { _id: '3', name: 'Submitted' },
      { _id: '4', name: 'Cancelled' },
    ],
  });
  allProjects$ = new BehaviorSubject<Project[]>([]);
  selectedProjectTotalCosts$ = new BehaviorSubject<{
    recorded: number;
    expected: number;
  }>({ expected: 0, recorded: 0 });

  searchParam$ = new BehaviorSubject<string>('');

  private languageSubscription: Subscription;

  constructor(
    private mediaService: MediaService,
    private financeService: FinanceService,
    private costItemService: CostItemService,
    private itemGroupService: ItemGroupService,
    private projectManagerService: ProjectManagerService,
    private communicationManagerService: CommunicationManagerService,
    private localStorageService: LocalStorageService,
    private loader: LoadingService,
    private translateService: TranslateService
  ) {
    this.updateStatusNames();
    
    // Subscribe to language changes
    this.languageSubscription = this.translateService.onLangChange.subscribe(() => {
      this.updateStatusNames();
    });
  }

  ngOnDestroy() {
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }

  saveCostItem(costItem: CompanyCostItem) {
    return this.communicationManagerService.post(
      this.companyItemRoute,
      costItem
    );
  }

  calculateProjectTotalCosts(
    groupDataInfo: {
      date: string;
      total: number;
      status: string;
      budgetedTotal: number;
    }[]
  ) {
    const totals = {
      recorded: 0,
      expected: 0,
    };
    groupDataInfo.forEach((item) => {
      totals.expected += item.budgetedTotal;
      totals.recorded += item.total;
    });
    this.selectedProjectTotalCosts$.next(totals);
  }

  private getInvoiceForProject(projectId: string) {
    return this.communicationManagerService.get(this.invoiceRoute, [
      { headerName: 'projectId', headerValue: projectId },
    ]) as Observable<InvoiceData[]>;
  }

  getAllProjects(companyId: string) {
    // this.loadingService.setLoading(true);
    return (
      this.projectManagerService
        .getAllProjects(companyId)
        // .get(this.projectRoute + '/all')
        .pipe(
          switchMap((projects: Project[]) => {
            const companyProjects = projects.filter(
              (projectItem) =>
                this.localStorageService.getCurrentCompany()?._id ===
                projectItem.company
            );
            return of(companyProjects);
            // const data = companyProjects.map((project) => {
            //   this.loader.startLoadingSpinner();
            //   return this.getInvoiceForProject(project._id).pipe(
            //     map((invoices) => {
            //       return {
            //         ...project,
            //         invoice: invoices[invoices.length - 1],
            //       } as Project;
            //     })
            //   );
            // });
            // return forkJoin(data);
          }),
          tap((projects) => {
            // console.log('HERE', projects.length);
            this.allProjects$.next(projects);
            // this.loadingService.setLoading(false);
          })
        )
    );
  }

  getFilteredProjects(): Observable<Project[]> {
    return this.allProjects$.pipe(
      tap((projects) => {
        this.updateCostsAndFilters(projects);
      }),
      switchMap((projects) => {
        return this.projectFilter$.pipe(
          map((filter) => {
            return projects.filter((project) => {
              return this.checkValues(project, filter);
            });
          })
        );
      })
    );
  }

  onSearchProjects(searchParam: string) {
    this.searchParam$.next(searchParam);
  }

  getOrCreateProjectCost(projectId: string, companyId: string) {
    return this.projectManagerService.getOrCreateProjectCost(
      projectId,
      companyId
    ) as Observable<string>;
  }

  getCompanyFilters() {
    return this.financeService.getCompanyFilters(true);
  }

  getItemGroups(projectId: string) {
    return this.itemGroupService.getAllProjectItemGroups(
      projectId
    ) as Observable<ItemGroup[]>;
  }

  getCostItems(projectId: string) {
    return this.costItemService.getCostItemsByProject(projectId) as Observable<
      CostItem[]
    >;
  }

  getMedia(projectId: string) {
    return this.mediaService.getProjectMedia(projectId) as Observable<
      ProjectMedia[]
    >;
  }

  private checkValues(project: Project, filter: ProjectFilter): boolean {
    for (const key in filter) {
      // eslint-disable-next-line no-prototype-builtins
      if (key === 'projectStatus') {
        if (filter?.hasOwnProperty(key) && project?.hasOwnProperty('status')) {
          const filterObj = filter[key as keyof ProjectFilter];
          const filterVal = (filterObj as any)._id;
          if (filterVal === 'All') return true;

          return project.status === filterVal;
        }
      } else if (filter?.hasOwnProperty(key) && project?.hasOwnProperty(key)) {
        const filterValue = filter[key as keyof ProjectFilter];
        const itemValue = project[key as keyof Project];
        if (typeof filterValue === 'object' && typeof itemValue === 'object') {
          if (
            !this.checkValues(
              itemValue as Project,
              filterValue as ProjectFilter
            )
          ) {
            return false;
          }
        } else {
          if (
            itemValue !== filterValue &&
            typeof filterValue === 'string' &&
            filterValue !== 'All'
          ) {
            return false;
          }
        }
      }
    }
    return true;
  }

  private updateCostsAndFilters(projects: Project[]) {
    const filters = {
      clients: [
        {
          _id: 'All',
          name: 'All',
        },
      ],
      budgetTypes: ['All'],
      projectStatuses: [
        { _id: 'All', name: 'All' },
        { _id: '0', name: this.translateService.instant('Pitching') },
        { _id: '1', name: this.translateService.instant('Ongoing') },
        { _id: '2', name: this.translateService.instant('Completed') },
        { _id: '3', name: this.translateService.instant('Submitted') },
        { _id: '4', name: this.translateService.instant('Cancelled') },
      ],
    };
    const totalCost = projects.reduce(
      (totals, project) => {
        const clientIndex = filters.clients.findIndex(
          (client) => client?._id === project.client?._id
        );
        const budgetTypeIndex = filters.budgetTypes.findIndex(
          (budgetType) => project.budgetType === budgetType
        );
        if (clientIndex === -1) {
          if (project?.client) filters.clients.push(project.client);
        }
        if (budgetTypeIndex === -1) {
          if (project?.budgetType) filters.budgetTypes.push(project.budgetType);
        }
        totals.expected += project.expectedBudget ?? 0;
        totals.recorded += project.total ?? 0;
        return totals;
      },
      { expected: 0, recorded: 0 }
    );
    this.filters$.next(filters);
    this.totalConst$.next(totalCost);
  }

  private updateStatusNames() {
    const statuses = [
      { _id: 'All', name: 'All' },
      { _id: '0', name: this.translateService.instant('Pitching') },
      { _id: '1', name: this.translateService.instant('Ongoing') },
      { _id: '2', name: this.translateService.instant('Completed') },
      { _id: '3', name: this.translateService.instant('Submitted') },
      { _id: '4', name: this.translateService.instant('Cancelled') },
    ];

    this.filters$.next({
      ...this.filters$.value,
      projectStatuses: statuses,
    });
  }
}
